import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Grid,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Chip,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tab,
    Tabs,
    Autocomplete,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import dayjs from 'dayjs'

import PageContainer from '@/components/ui/containers/PageContainer'
import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import { MedicalActRecord } from '@/core/models/medical-act.model'
import { Establishment } from '@/core/models/establishment.model'
import {
    useGetMedicalActRecordsQuery,
    useGetMedicalActStatisticsQuery,
    useDeleteMedicalActRecordMutation,
    useGetMedicalActTemplatesQuery,
} from '@/core/services/medical-act'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { getEstablishments, selectEstablishments } from '@/redux/reducers/admin.reducer'
import { formatPrice } from '@/utils/formatPrice'


export function MedicalActRecordsPage() {
    const { t } = useTranslation()
    const isSuperAdmin = useLocation().pathname.startsWith('/super-admin')

    // State for filters
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(20)
    const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(dayjs().startOf('month'))
    const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(dayjs().endOf('month'))
    const [selectedTemplate, setSelectedTemplate] = useState<string>('')
    const [selectedEstablishment, setSelectedEstablishment] = useState<Establishment | null>(null)

    // Queries
    const { data: records, isLoading: isLoadingRecords, refetch } = useGetMedicalActRecordsQuery({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        ...(selectedTemplate && { template: selectedTemplate }),
        ...(selectedEstablishment && { establishment: selectedEstablishment._id }),
    })

    const { data: statistics, isLoading: isLoadingStats } = useGetMedicalActStatisticsQuery({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        ...(selectedEstablishment && { establishment: selectedEstablishment._id }),
    })

    const { data: templates } = useGetMedicalActTemplatesQuery()

    // Establishment fetching (Redux)
    const dispatch = useAppDispatch()
    const establishments = useAppSelector(selectEstablishments)

    const navigate = useNavigate()

    useEffect(() => {
        if (isSuperAdmin) {
            dispatch(getEstablishments())
        }
    }, [dispatch, isSuperAdmin])

    const [deleteRecord, { isLoading: isDeleting }] = useDeleteMedicalActRecordMutation()

    // Pagination
    const paginatedRecords = useMemo(() => {
        if (!records) return []
        return records.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    }, [records, page, rowsPerPage])

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleDelete = async (recordId: string) => {
        if (window.confirm(t('medicalAct.record.deleteConfirm', { defaultValue: 'Delete this medical act record?' }))) {
            try {
                await deleteRecord(recordId).unwrap()
                refetch()
            } catch (error) {
                // Error handled by RTK Query
            }
        }
    }

    const formatDate = (date: string) => {
        return dayjs(date).format('DD/MM/YYYY HH:mm')
    }

    const handleExportCSV = () => {
        if (!records || records.length === 0) return

        const headers = ['Date', 'Establishment', 'Act', 'Code', 'Patient', 'Performer', 'Price', 'Context']
        const rows = records.map((record) => {
            const template = typeof record.template === 'object' ? record.template : null
            const patient = typeof record.patient === 'object' ? record.patient : null
            const performer = typeof record.performedBy === 'object' ? record.performedBy : null
            const establishment = typeof record.establishment === 'object' ? record.establishment : null

            const context = record.consultation
                ? 'Consultation'
                : record.expertise
                    ? 'Expertise'
                    : 'Standalone'

            return [
                formatDate(record.performedAt),
                establishment?.name || 'Unknown',
                template?.name || 'Unknown',
                template?.code || '',
                patient?.userInfo?.name && patient?.userInfo?.surname
                    ? `${patient.userInfo.name} ${patient.userInfo.surname}`
                    : 'Unknown',
                performer?.userInfo?.name && performer?.userInfo?.surname
                    ? `${performer.userInfo.name} ${performer.userInfo.surname}`
                    : 'Unknown',
                record.priceAtTime,
                context,
            ]
        })

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `medical-acts-${dayjs().format('YYYY-MM-DD')}.csv`
        link.click()
    }

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        const basePath = location.pathname.startsWith('/super-admin') ? '/super-admin' : '/admin'
        if (newValue === 0) {
            navigate(`${basePath}/medical-acts`)
        }
    }

    const isLoading = isLoadingRecords || isLoadingStats

    return (
        <PageContainer>
            <Typography variant="h4" component="h1" gutterBottom>
                {t('page.admin.medicalActs.title', { defaultValue: 'Medical Acts' })}
            </Typography>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={1} onChange={handleTabChange}>
                    <Tab label={t('page.admin.medicalActs.tabs.templates', { defaultValue: 'Templates' })} />
                    <Tab label={t('page.admin.medicalActs.tabs.records', { defaultValue: 'Records' })} />
                </Tabs>
            </Box>

            <Stack spacing={3}>
                {/* Header with Export */}
                <Stack direction="row" justifyContent="flex-end" alignItems="center">
                    <Tooltip title={t('medicalAct.records.exportCSV', { defaultValue: 'Export to CSV' })}>
                        <IconButton
                            color="primary"
                            onClick={handleExportCSV}
                            disabled={!records || records.length === 0}
                        >
                            <FileDownloadIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>

                {/* Statistics Cards */}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t('medicalAct.stats.totalActs', { defaultValue: 'Total Acts' })}
                                </Typography>
                                <Typography variant="h4" fontWeight="bold" color="primary">
                                    {isLoadingStats ? <CircularProgress size={24} /> : statistics?.totalActs || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t('medicalAct.stats.totalRevenue', { defaultValue: 'Total Revenue' })}
                                </Typography>
                                <Typography variant="h4" fontWeight="bold" color="success.main">
                                    {isLoadingStats ? <CircularProgress size={24} /> : formatPrice(statistics?.totalRevenue || 0)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t('medicalAct.stats.uniqueTypes', { defaultValue: 'Unique Act Types' })}
                                </Typography>
                                <Typography variant="h4" fontWeight="bold" color="info.main">
                                    {isLoadingStats ? <CircularProgress size={24} /> : statistics?.statistics?.length || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Filters */}
                <Paper sx={{ p: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                            <DatePickerInput
                                label={t('common.startDate', { defaultValue: 'Start Date' })}
                                value={startDate}
                                onChange={setStartDate}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <DatePickerInput
                                label={t('common.endDate', { defaultValue: 'End Date' })}
                                value={endDate}
                                onChange={setEndDate}
                                fullWidth
                            />
                        </Grid>
                        {isSuperAdmin && (
                            <Grid item xs={12} sm={3}>
                                <FormControl fullWidth size="small">
                                    <Autocomplete
                                        options={establishments || []}
                                        getOptionLabel={(option) => option.name}
                                        value={selectedEstablishment}
                                        onChange={(_, newValue) => setSelectedEstablishment(newValue)}
                                        renderInput={(params) => <TextField {...params} label={t('common.establishment', { defaultValue: 'Establishment' })} />}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                        )}
                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('medicalAct.records.filterByTemplate', { defaultValue: 'Filter by Template' })}</InputLabel>
                                <Select
                                    value={selectedTemplate}
                                    onChange={(e) => setSelectedTemplate(e.target.value)}
                                    label={t('medicalAct.records.filterByTemplate', { defaultValue: 'Filter by Template' })}
                                >
                                    <MenuItem value="">
                                        <em>{t('common.all', { defaultValue: 'All' })}</em>
                                    </MenuItem>
                                    {templates?.map((template) => (
                                        <MenuItem key={template._id} value={template._id}>
                                            {template.name} ({template.code})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                                {records?.length || 0} {t('medicalAct.records.resultsFound', { defaultValue: 'records found' })}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Records Table */}
                <TableContainer component={Paper}>
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : !records || records.length === 0 ? (
                        <Alert severity="info" sx={{ m: 2 }}>
                            {t('medicalAct.records.noRecords', { defaultValue: 'No medical acts found for the selected period' })}
                        </Alert>
                    ) : (
                        <>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('medicalAct.records.date', { defaultValue: 'Date' })}</TableCell>
                                        {isSuperAdmin && <TableCell>{t('common.establishment', { defaultValue: 'Establishment' })}</TableCell>}
                                        <TableCell>{t('medicalAct.records.act', { defaultValue: 'Act' })}</TableCell>
                                        <TableCell>{t('medicalAct.records.patient', { defaultValue: 'Patient' })}</TableCell>
                                        <TableCell>{t('medicalAct.records.performer', { defaultValue: 'Performer' })}</TableCell>
                                        <TableCell>{t('medicalAct.records.context', { defaultValue: 'Context' })}</TableCell>
                                        <TableCell align="right">{t('medicalAct.records.price', { defaultValue: 'Price' })}</TableCell>
                                        <TableCell align="center">{t('common.actions', { defaultValue: 'Actions' })}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedRecords.map((record) => {
                                        const template = typeof record.template === 'object' ? record.template : null
                                        const patient = typeof record.patient === 'object' ? record.patient : null
                                        const performer = typeof record.performedBy === 'object' ? record.performedBy : null
                                        const establishment = typeof record.establishment === 'object' ? record.establishment : null

                                        const context = record.consultation
                                            ? 'Consultation'
                                            : record.expertise
                                                ? 'Expertise'
                                                : 'Standalone'

                                        return (
                                            <TableRow key={record._id} hover>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatDate(record.performedAt)}
                                                    </Typography>
                                                </TableCell>
                                                {isSuperAdmin && (
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {establishment?.name || 'Unknown'}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {template?.name || 'Unknown'}
                                                        </Typography>
                                                        <Chip label={template?.code || '?'} size="small" variant="outlined" />
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {patient?.userInfo?.name} {patient?.userInfo?.surname}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {performer?.userInfo?.name} {performer?.userInfo?.surname}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={context}
                                                        size="small"
                                                        color={context === 'Consultation' ? 'primary' : context === 'Expertise' ? 'secondary' : 'default'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" fontWeight="bold" color="success.main">
                                                        {formatPrice(record.priceAtTime)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title={t('common.delete', { defaultValue: 'Delete' })}>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDelete(record._id)}
                                                            disabled={isDeleting}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[10, 20, 50, 100]}
                                component="div"
                                count={records.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage={t('common.rowsPerPage', { defaultValue: 'Rows per page' })}
                            />
                        </>
                    )}
                </TableContainer>
            </Stack>
        </PageContainer>
    )
}

export default MedicalActRecordsPage

